import { Component } from 'react'
import PropTypes from 'prop-types'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import { AlertCircle } from 'lucide-react'
import L, { divIcon } from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Custom cluster marker styles
const clusterStyles = `
  .ai-cluster-marker {
    background-color: #3b82f6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 35px;
    height: 35px;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  }

  .ai-cluster-count {
    color: white;
    font-weight: bold;
    font-size: 14px;
    font-family: 'Poppins', system-ui, sans-serif;
  }

  .ai-custom-cluster-icon {
    background: none;
    border: none;
  }
`

// Inject styles once
if (!document.getElementById('ai-cluster-styles')) {
  const styleSheet = document.createElement('style')
  styleSheet.id = 'ai-cluster-styles'
  styleSheet.textContent = clusterStyles
  document.head.appendChild(styleSheet)
}

// Fix default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

/**
 * AiLeafletViz Component
 *
 * Renders Leaflet maps from API config with error boundary
 */
class AiLeafletViz extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
    }
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error: error.message,
    }
  }

  componentDidCatch(error, errorInfo) {
    console.error('Leaflet rendering error:', error, errorInfo)
  }

  render() {
    const { config, title } = this.props
    const { hasError, error } = this.state

    if (hasError) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 bg-red-50 rounded-lg border border-red-200">
          <AlertCircle className="w-8 h-8 text-red-600 mb-2" />
          <p className="text-sm font-medium text-red-900 mb-1">
            Unable to render map: {title}
          </p>
          <p className="text-xs text-red-700">
            {error || 'An error occurred while rendering this visualization'}
          </p>
        </div>
      )
    }

    // Validate config
    if (
      !config.center ||
      !Array.isArray(config.center) ||
      config.center.length !== 2
    ) {
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <AlertCircle className="w-8 h-8 text-yellow-600 mb-2" />
          <p className="text-sm font-medium text-yellow-900 mb-1">
            Invalid map configuration
          </p>
          <p className="text-xs text-yellow-700">
            Map center coordinates are missing or invalid
          </p>
        </div>
      )
    }

    try {
      const zoom = config.zoom || 10
      const markers = config.markers || []

      return (
        <div
          className="rounded-lg overflow-hidden border border-gray-300"
          style={{ height: '400px' }}
        >
          <MapContainer
            center={config.center}
            zoom={zoom}
            style={{
              height: '100%',
              width: '100%',
              fontFamily: 'Poppins, system-ui, sans-serif',
            }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Render markers with clustering */}
            <MarkerClusterGroup
              chunkedLoading
              maxClusterRadius={50}
              disableClusteringAtZoom={15}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              zoomToBoundsOnClick={true}
              iconCreateFunction={cluster => {
                const count = cluster.getChildCount()
                return divIcon({
                  html: `<div class="ai-cluster-marker">
                           <span class="ai-cluster-count">${count}</span>
                         </div>`,
                  className: 'ai-custom-cluster-icon',
                  iconSize: [35, 35],
                  iconAnchor: [17, 17],
                })
              }}
            >
              {markers.map((marker, index) => {
                if (
                  !marker.position ||
                  !Array.isArray(marker.position) ||
                  marker.position.length !== 2
                ) {
                  console.warn(
                    `Invalid marker position at index ${index}:`,
                    marker
                  )
                  return null
                }

                return (
                  <Marker key={index} position={marker.position}>
                    {marker.popup && (
                      <Popup>
                        <div
                          className="text-sm"
                          dangerouslySetInnerHTML={{ __html: marker.popup }}
                        />
                      </Popup>
                    )}
                  </Marker>
                )
              })}
            </MarkerClusterGroup>
          </MapContainer>
        </div>
      )
    } catch (err) {
      console.error('Leaflet render error:', err)
      return (
        <div className="flex flex-col items-center justify-center py-12 px-4 bg-red-50 rounded-lg border border-red-200">
          <AlertCircle className="w-8 h-8 text-red-600 mb-2" />
          <p className="text-sm font-medium text-red-900 mb-1">
            Unable to render map: {title}
          </p>
          <p className="text-xs text-red-700">{err.message}</p>
        </div>
      )
    }
  }
}

AiLeafletViz.propTypes = {
  config: PropTypes.shape({
    center: PropTypes.arrayOf(PropTypes.number).isRequired,
    zoom: PropTypes.number,
    markers: PropTypes.arrayOf(
      PropTypes.shape({
        position: PropTypes.arrayOf(PropTypes.number).isRequired,
        popup: PropTypes.string,
        icon: PropTypes.string,
        color: PropTypes.string,
      })
    ),
  }).isRequired,
  title: PropTypes.string.isRequired,
}

export default AiLeafletViz
